// src/pages/pacs/GodownApplyForm.js

import { useState } from "react";
import { Link } from "react-router-dom";
import { db, storage, auth } from "../../firebaseConfig";
import {
  addDoc,
  collection,
  serverTimestamp,
  doc,
  runTransaction,
  getDocs,
  query,
  where,
  updateDoc,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import toast, { Toaster } from "react-hot-toast";
import jsPDF from "jspdf";
import dayjs from "dayjs";

/* ---------- Helpers & UI atoms ---------- */

const GUJ_DISTRICTS = [
  "Banaskantha",
  "VavTharad",
  "Mahesana",
  "Patan",
  "Gandhinagar",
  "Ahmedabad",
  "Sabarkantha",
  "Aravalli",
  "Kutch",
  "Surat",
  "Vadodara",
  "Bhavnagar",
  "Rajkot",
];

const TALUKAS_BY_DISTRICT = {
  Banaskantha: [
    "Amirgadh",
    "Dhanera",
    "Deesa",
    "Danta",
    "Dantiwada",
    "Palanpur",
    "Vadgam",
    "Kankrej",
    "Hadad",
    "Ogad",
  ],
  VavTharad: [
    "Vav",
    "Tharad",
    "Suigam",
    "Lakhni",
    "Diyodar",
    "Bhabhar",
    "Dharnidhar",
    "Rah",
  ],
  Mahesana: [
    "Mehsana",
    "Vijapur",
    "Kadi",
    "Visnagar",
    "Unjha",
    "Kheralu",
    "Becharaji",
    "Satlasna",
    "Jotana",
  ],
  Patan: [
    "Patan",
    "Siddhpur",
    "Harij",
    "Radhanpur",
    "Sami",
    "Santalpur",
    "Chanasma",
  ],
  Gandhinagar: ["Gandhinagar", "Kalol", "Mansa", "Dehgam"],
  Ahmedabad: [
    "Ahmedabad City",
    "Daskroi",
    "Sanand",
    "Bavla",
    "Dholka",
    "Dhandhuka",
    "Dholera",
    "Mandal",
    "Detroj-Rampura",
    "Viramgam",
  ],
  Sabarkantha: [
    "Himatnagar",
    "Idar",
    "Khedbrahma",
    "Talod",
    "Prantij",
    "Vadali",
    "Poshina",
  ],
  Aravalli: [
    "Modasa",
    "Malpur",
    "Meghraj",
    "Bhiloda",
    "Dhansura",
    "Bayad",
    "Shamlaji",
  ],
  Kutch: [
    "Bhuj",
    "Anjar",
    "Bhachau",
    "Gandhidham",
    "Mandvi",
    "Mundra",
    "Nakhatrana",
    "Rapar",
    "Abdasa",
    "Lakhpat",
  ],
  Surat: [
    "Surat City",
    "Kamrej",
    "Choryasi",
    "Palsana",
    "Bardoli",
    "Olpad",
    "Mahuva",
    "Mandvi",
    "Umarpada",
  ],
  Vadodara: [
    "Vadodara",
    "Karjan",
    "Dabhoi",
    "Padra",
    "Savli",
    "Sinor",
    "Vaghodia",
  ],
  Bhavnagar: [
    "Bhavnagar",
    "Sihor",
    "Talaja",
    "Gariadhar",
    "Ghogha",
    "Mahuva",
    "Palitana",
    "Umrala",
    "Vallabhipur",
  ],
  Rajkot: [
    "Rajkot",
    "Gondal",
    "Jasdan",
    "Jetpur",
    "Dhoraji",
    "Lodhika",
    "Kotda Sangani",
  ],
};

const CAPACITY_OPTIONS = [500, 750, 1000, 1500, 2000];

const cx = (...xs) => xs.filter(Boolean).join(" ");

function normalizeDistrictName(v = "") {
  const raw = String(v || "").trim();
  const compact = raw.replace(/[\s_-]+/g, "").toLowerCase();

  if (compact === "vavtharad") return "VavTharad";
  if (compact === "banaskantha") return "Banaskantha";
  if (compact === "mahesana" || compact === "mehsana") return "Mahesana";
  if (compact === "patan") return "Patan";
  if (compact === "gandhinagar") return "Gandhinagar";
  if (compact === "ahmedabad") return "Ahmedabad";
  if (compact === "sabarkantha") return "Sabarkantha";
  if (compact === "aravalli") return "Aravalli";
  if (compact === "kutch") return "Kutch";
  if (compact === "surat") return "Surat";
  if (compact === "vadodara") return "Vadodara";
  if (compact === "bhavnagar") return "Bhavnagar";
  if (compact === "rajkot") return "Rajkot";

  return raw;
}

function Label({ children }) {
  return (
    <label className="text-xs font-semibold text-slate-700 tracking-wide">
      {children}
    </label>
  );
}

function Input({ type = "text", value, onChange, ...p }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full rounded-xl border border-slate-200 bg-white/70 px-4 py-2.5 text-sm shadow-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
      {...p}
    />
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-3xl bg-white/95 shadow-[0_18px_40px_rgba(15,118,110,0.10)] border border-emerald-100/70 p-5 md:p-6">
      {title && (
        <h2 className="text-base md:text-lg font-bold text-slate-800 tracking-tight mb-4">
          {title}
        </h2>
      )}
      <div className="grid gap-4">{children}</div>
    </div>
  );
}

function toAmount(v) {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function formatInr(v) {
  return `₹${toAmount(v).toLocaleString("en-IN")}`;
}

function getFyString() {
  const today = new Date();
  const y = today.getFullYear();
  const month = today.getMonth();
  const fyStartYear = month >= 3 ? y : y - 1;
  const yy1 = String(fyStartYear).slice(2);
  const yy2 = String(fyStartYear + 1).slice(2);
  return yy1 + yy2;
}

function getFyLabel(startYear) {
  return `${startYear}-${String(startYear + 1).slice(2)}`;
}

function getLastCompletedFinancialYears(count = 7) {
  const today = new Date();
  const y = today.getFullYear();
  const month = today.getMonth();
  const currentFyStartYear = month >= 3 ? y : y - 1;
  const lastCompletedFyStartYear = currentFyStartYear - 1;

  return Array.from({ length: count }, (_, index) => {
    const startYear = lastCompletedFyStartYear - index;
    const value = getFyLabel(startYear);
    return {
      value,
      label: `FY ${value}`,
    };
  });
}

async function getNextGodownApplicationId() {
  const seriesRef = doc(db, "series", "godown_applications");
  const fy = getFyString();

  const result = await runTransaction(db, async (tx) => {
    const snap = await tx.get(seriesRef);
    let next = 1;

    if (snap.exists()) {
      const data = snap.data();
      const currentFy = data.fy || fy;

      if (currentFy === fy) next = Number(data.seq || 0) + 1;
      else next = 1;

      tx.update(seriesRef, {
        seq: next,
        fy,
        updatedAt: serverTimestamp(),
      });
    } else {
      tx.set(seriesRef, {
        seq: 1,
        fy,
        updatedAt: serverTimestamp(),
      });
      next = 1;
    }

    return next;
  });

  const num = String(result).padStart(4, "0");
  const appId = `SAAM/${fy}/Godown/${num}`;

  return { appId, seq: result, fy };
}

/* ---------- PDF: Premium Formal Letter + Financial Tables ---------- */
function downloadApplicationPDF(data) {
  const pdf = new jsPDF({ unit: "pt", format: "a4" });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const marginX = 48;
  const contentWidth = pageWidth - marginX * 2;
  const bottomY = pageHeight - 42;

  const safe = (v) => String(v == null ? "" : v);

  const toNum = (v) => {
    const n = Number(String(v ?? "").replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
  };

  const moneyPdf = (v) => `Rs. ${toNum(v).toLocaleString("en-IN")}`;

  const financial = data.financialDetails || {};
  const profitsRaw = Array.isArray(financial.profits) ? financial.profits : [];

  const profits =
    profitsRaw.length > 0
      ? profitsRaw.slice(0, 3)
      : [
          { fy: "Year 1", profit: 0 },
          { fy: "Year 2", profit: 0 },
          { fy: "Year 3", profit: 0 },
        ];

  const totalProfit =
    financial?.totals?.totalProfit != null
      ? toNum(financial.totals.totalProfit)
      : profits.reduce((sum, row) => sum + toNum(row.profit), 0);

  const averageProfit =
    financial?.totals?.averageProfit != null
      ? toNum(financial.totals.averageProfit)
      : Math.round(totalProfit / Math.max(profits.length, 1));

  const totalFunds =
    financial?.totals?.totalFunds != null
      ? toNum(financial.totals.totalFunds)
      : toNum(financial.reserveFund) +
        toNum(financial.buildingFund) +
        toNum(financial.shareCapital);

  let y = 48;

  const addFooter = () => {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(120, 120, 120);
    pdf.text(
      "System-generated application letter. Final approval / subsidy / finance is subject to verification by competent authorities.",
      marginX,
      pageHeight - 22
    );
    pdf.setTextColor(0, 0, 0);
  };

  const newPage = () => {
    addFooter();
    pdf.addPage();
    y = 48;
  };

  const ensureSpace = (needed = 60) => {
    if (y + needed > bottomY) newPage();
  };

  const writeWrapped = ({
    text,
    x = marginX,
    width = contentWidth,
    lineHeight = 13,
    font = "normal",
    size = 10.5,
    color = [0, 0, 0],
  }) => {
    pdf.setFont("helvetica", font);
    pdf.setFontSize(size);
    pdf.setTextColor(color[0], color[1], color[2]);

    const lines = pdf.splitTextToSize(safe(text), width);
    pdf.text(lines, x, y);

    y += lines.length * lineHeight;
    pdf.setTextColor(0, 0, 0);

    return lines;
  };

  const drawMainHeader = () => {
    pdf.setFillColor(236, 253, 245);
    pdf.setDrawColor(16, 185, 129);
    pdf.setLineWidth(1.2);
    pdf.roundedRect(marginX, 26, contentWidth, 62, 10, 10, "FD");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.setTextColor(5, 95, 70);
    pdf.text("GODOWN PROJECT APPLICATION / REQUEST LETTER", pageWidth / 2, 51, {
      align: "center",
    });

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9.5);
    pdf.setTextColor(70, 70, 70);
    pdf.text(
      "World's Largest Grain Storage Plan in Cooperative Sector",
      pageWidth / 2,
      68,
      { align: "center" }
    );

    pdf.setTextColor(0, 0, 0);
    y = 112;
  };

  const drawApplicationDetailsBox = () => {
    const boxTop = y;
    const boxH = 72;

    pdf.setFillColor(248, 250, 252);
    pdf.setDrawColor(190, 190, 190);
    pdf.roundedRect(marginX, boxTop, contentWidth, boxH, 8, 8, "FD");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10.5);
    pdf.text("Application Details", marginX + 14, boxTop + 19);

    const appDate = dayjs(data.createdAt || new Date()).format("DD/MM/YYYY");

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9.5);

    pdf.text(`Application ID: ${safe(data.applicationId)}`, marginX + 14, boxTop + 39);
    pdf.text(`Date: ${appDate}`, marginX + 355, boxTop + 39);
    pdf.text(
      `Financial Year: ${safe(data.financialYear || getFyString())}`,
      marginX + 14,
      boxTop + 56
    );

    y = boxTop + boxH + 24;
  };

  const drawSimpleInfoBox = (title, rows) => {
    ensureSpace(110);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.setTextColor(5, 95, 70);
    pdf.text(title, marginX, y);
    pdf.setTextColor(0, 0, 0);
    y += 14;

    const rowH = 20;
    const boxTop = y;
    const boxH = rows.length * rowH + 18;

    pdf.setFillColor(248, 250, 252);
    pdf.setDrawColor(190, 190, 190);
    pdf.roundedRect(marginX, boxTop, contentWidth, boxH, 8, 8, "FD");

    let rowY = boxTop + 20;

    rows.forEach(([label, value]) => {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9.5);
      pdf.text(`${label}:`, marginX + 14, rowY);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9.5);
      const valueLines = pdf.splitTextToSize(safe(value || "-"), contentWidth - 185);
      pdf.text(valueLines, marginX + 150, rowY);

      rowY += Math.max(rowH, valueLines.length * 11 + 6);
    });

    y = boxTop + boxH + 22;
  };

  const drawProfitTable = () => {
    ensureSpace(190);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.setTextColor(20, 20, 20);
    pdf.text("Last 3 Years Profit Details", marginX, y);
    y += 16;

    const tableX = marginX;
    const tableW = contentWidth;

    const colSr = 50;
    const colFy = 250;
    const colAmt = tableW - colSr - colFy;

    const rowH = 27;

    const drawRow = ({
      sr = "",
      fy = "",
      amount = "",
      isHeader = false,
      isTotal = false,
    }) => {
      ensureSpace(rowH + 10);

      const rowTop = y;

      if (isHeader) {
        pdf.setFillColor(232, 248, 239);
      } else if (isTotal) {
        pdf.setFillColor(236, 253, 245);
      } else {
        pdf.setFillColor(255, 255, 255);
      }

      pdf.setDrawColor(165, 165, 165);
      pdf.rect(tableX, rowTop, tableW, rowH, "FD");

      pdf.line(tableX + colSr, rowTop, tableX + colSr, rowTop + rowH);
      pdf.line(tableX + colSr + colFy, rowTop, tableX + colSr + colFy, rowTop + rowH);

      pdf.setFont("helvetica", isHeader || isTotal ? "bold" : "normal");
      pdf.setFontSize(9.5);
      pdf.setTextColor(0, 0, 0);

      const textY = rowTop + 17;

      pdf.text(safe(sr), tableX + 12, textY);
      pdf.text(safe(fy), tableX + colSr + 12, textY);
      pdf.text(safe(amount), tableX + tableW - 12, textY, { align: "right" });

      y += rowH;
    };

    drawRow({
      sr: "Sr.",
      fy: "Financial Year",
      amount: "Net Profit",
      isHeader: true,
    });

    profits.forEach((item, index) => {
      drawRow({
        sr: String(index + 1),
        fy: item.fy || `Year ${index + 1}`,
        amount: moneyPdf(item.profit),
      });
    });

    drawRow({
      sr: "",
      fy: "Total Profit (3 Years)",
      amount: moneyPdf(totalProfit),
      isTotal: true,
    });

    drawRow({
      sr: "",
      fy: "Average Annual Profit",
      amount: moneyPdf(averageProfit),
      isTotal: true,
    });

    y += 26;
  };

  const drawFundTable = () => {
    ensureSpace(205);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.setTextColor(20, 20, 20);
    pdf.text("Fund / Capital Summary", marginX, y);
    y += 16;

    const rows = [
      ["Reserve Fund", moneyPdf(financial.reserveFund)],
      ["Building / Makan Fund", moneyPdf(financial.buildingFund)],
      ["Share Capital", moneyPdf(financial.shareCapital)],
      ["Total Fund Strength", moneyPdf(totalFunds)],
      ["Audit Class", safe(financial.auditClass || "-")],
    ];

    const tableX = marginX;
    const tableW = contentWidth;
    const colParticular = 330;
    const rowH = 28;

    const drawRow = ({ label, value, isHeader = false, isTotal = false }) => {
      ensureSpace(rowH + 10);

      const rowTop = y;

      if (isHeader) {
        pdf.setFillColor(255, 251, 235);
      } else if (isTotal) {
        pdf.setFillColor(254, 243, 199);
      } else {
        pdf.setFillColor(255, 255, 255);
      }

      pdf.setDrawColor(175, 175, 175);
      pdf.rect(tableX, rowTop, tableW, rowH, "FD");
      pdf.line(tableX + colParticular, rowTop, tableX + colParticular, rowTop + rowH);

      pdf.setFont("helvetica", isHeader || isTotal ? "bold" : "normal");
      pdf.setFontSize(9.8);
      pdf.setTextColor(0, 0, 0);

      const textY = rowTop + 18;

      pdf.text(safe(label), tableX + 12, textY);
      pdf.text(safe(value), tableX + tableW - 12, textY, { align: "right" });

      y += rowH;
    };

    drawRow({
      label: "Particular",
      value: "Amount / Class",
      isHeader: true,
    });

    rows.forEach(([label, value]) => {
      drawRow({
        label,
        value,
        isTotal: label === "Total Fund Strength",
      });
    });

    y += 26;
  };

  const drawChecklist = () => {
    ensureSpace(210);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.text("Document Checklist", marginX, y);
    y += 18;

    const checklist = [
      "Registration Certificate (latest).",
      "Land documents: 7/12, 8A, sale deed / lease deed, mutation entries etc. as applicable.",
      "Board / Managing Committee Resolution for godown readiness and authorization.",
      "Last 3 years Audit Reports / Financial Statements.",
      "Financial details: reserve fund, building fund, share capital and last 3 years profit statement.",
      "Any other supporting documents: photos, site map, approvals etc. if applicable.",
    ];

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);

    checklist.forEach((item, index) => {
      ensureSpace(35);

      pdf.setDrawColor(185, 185, 185);
      pdf.rect(marginX, y - 10, 11, 11);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      pdf.text(`${index + 1}.`, marginX + 22, y);

      const lines = pdf.splitTextToSize(item, contentWidth - 55);
      pdf.text(lines, marginX + 45, y);

      y += Math.max(25, lines.length * 13 + 6);
    });

    y += 12;

    ensureSpace(70);

    pdf.setDrawColor(180, 180, 180);
    pdf.line(marginX, y, pageWidth - marginX, y);
    y += 18;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9.5);

    const declaration =
      "Declaration: We certify that the information provided in this application and financial statement is true to the best of our knowledge. Any misrepresentation may lead to rejection as per applicable norms.";

    const declLines = pdf.splitTextToSize(declaration, contentWidth);
    pdf.text(declLines, marginX, y);
    y += declLines.length * 13;
  };

  /* ================= PAGE 1: FORMAL LETTER ================= */

  drawMainHeader();
  drawApplicationDetailsBox();

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10.5);
  pdf.setTextColor(0, 0, 0);

  pdf.text("To,", marginX, y);
  y += 14;

  pdf.text("The District Registrar,", marginX, y);
  y += 14;

  pdf.text("Co-operative Societies,", marginX, y);
  y += 14;

  pdf.text(`${safe(data.district)}, ${safe(data.state || "Gujarat")}.`, marginX, y);
  y += 22;

  writeWrapped({
    text:
      "Subject: Request for approval / recommendation for construction of Grain Storage Godown under the World's Largest Grain Storage Plan in Cooperative Sector.",
    font: "bold",
    size: 10.5,
    lineHeight: 13,
  });

  y += 10;

  const letterBody =
    "Respected Sir/Madam,\n\n" +
    "We, the undersigned applicant institution, hereby submit this application/request for construction of a " +
    safe(data.capacityMt) +
    " MT grain storage godown under the Government of India's World's Largest Grain Storage Plan in Cooperative Sector, strictly as per applicable Margdarshika / scheme guidelines.\n\n" +
    "The proposed project location is Village: " +
    safe(data.village) +
    ", Taluka: " +
    safe(data.taluka) +
    ", District: " +
    safe(data.district) +
    ", State: " +
    safe(data.state || "Gujarat") +
    ".\n\n" +
    "We have engaged Shine Agri Allied & Marketing Multistate Cooperative Society Ltd. as Project Management Consultant (PMC) for DPR support, documentation, survey coordination, design, estimate, tender assistance, approvals coordination and implementation monitoring.\n\n" +
    "We request your good office to kindly examine our proposal and provide necessary approval / recommendation as required under the scheme process, so that further steps like DPR finalization, DCDC processing, bank finance and other compliances may be initiated in a time-bound manner.\n\n" +
    "A financial strength summary of the applicant institution is enclosed as Annexure-A, including last three years' profit details and average annual profit.\n\n" +
    "Thanking you,\nYours faithfully,";

  writeWrapped({
    text: letterBody,
    font: "normal",
    size: 10.5,
    lineHeight: 13,
  });

  y += 18;

  ensureSpace(95);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10.5);
  pdf.text("For and on behalf of the Applicant Institution", marginX, y);
  y += 18;

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);

  pdf.text("Institution Name: " + safe(data.pacsName), marginX, y);
  y += 14;

  pdf.text("Registration No.: " + safe(data.pacsRegistrationNo), marginX, y);
  y += 14;

  pdf.text("Authorized Signatory: ______________________________", marginX, y);
  y += 14;

  pdf.text("Name & Designation: ______________________________", marginX, y);
  y += 14;

  pdf.text("Mobile: " + safe(data.contactPhone), marginX, y);
  y += 14;

  pdf.text("Seal: ______________________________", marginX, y);

  addFooter();

  /* ================= PAGE 2: FINANCIAL STATEMENT ================= */

  pdf.addPage();
  y = 48;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(15);
  pdf.setTextColor(5, 95, 70);
  pdf.text("Annexure-A: Financial Strength Statement", pageWidth / 2, y, {
    align: "center",
  });

  pdf.setTextColor(0, 0, 0);
  y += 30;

  writeWrapped({
    text:
      "This summary is submitted for preliminary verification of the applicant institution's financial capacity. Amounts are declared by the applicant institution and shall be verified from audit reports / financial statements by competent authorities.",
    font: "normal",
    size: 10,
    lineHeight: 13,
  });

  y += 18;

  drawSimpleInfoBox("Applicant Institution Details", [
    ["Institution", data.pacsName],
    ["Registration No.", data.pacsRegistrationNo],
    ["Location", `${safe(data.village)}, ${safe(data.taluka)}, ${safe(data.district)}`],
    ["Proposed Capacity", `${safe(data.capacityMt)} MT`],
  ]);

  drawProfitTable();
  drawFundTable();
  drawChecklist();

  addFooter();

  const fileName = safe(data.applicationId || "godown_application").replace(/\//g, "_");
  pdf.save(fileName + ".pdf");
}

/* ---------- Helper: Queue notifications ---------- */
async function queuePmcNotificationOnNewApplication(payload) {
  try {
    await addDoc(collection(db, "notifications_pmc"), {
      type: "GODOWN_APPLICATION_SUBMITTED",
      applicationId: payload.applicationId,
      pacsName: payload.pacsName,
      pacsRegistrationNo: payload.pacsRegistrationNo,
      district: payload.district,
      taluka: payload.taluka,
      village: payload.village,
      capacityMt: payload.capacityMt,
      contactName: payload.contactName,
      contactPhone: payload.contactPhone,
      source: "PACS",
      status: "pending",
      createdAt: serverTimestamp(),
    });
  } catch (e) {
    console.error("Notification queue failed:", e);
  }
}

/* ---------- Link apply to outreach / unmatched ---------- */
async function linkApplicationToOutreachOrUnmatched({
  invitationCode,
  applicationId,
  pacsName,
  contactName,
  contactPhone,
  contactEmail,
  district,
  taluka,
  village,
  capacityMt,
}) {
  const now = serverTimestamp();
  const code = String(invitationCode || "").trim();

  if (code) {
    try {
      const q1 = query(
        collection(db, "outreach_recipients"),
        where("uniqueCode", "==", code)
      );

      const snap = await getDocs(q1);

      if (!snap.empty) {
        const d = snap.docs[0];

        await updateDoc(doc(db, "outreach_recipients", d.id), {
          outreachStatus: "APPLIED",
          appliedApplicationId: applicationId,
          appliedAt: now,
          updatedAt: now,

          societyName: pacsName || d.data()?.societyName || "",
          district: district || d.data()?.district || "",
          taluka: taluka || d.data()?.taluka || "",
          village: village || d.data()?.village || "",
          mobile: (contactPhone || "").trim() || d.data()?.mobile || "",
          email: (contactEmail || "").trim() || d.data()?.email || "",
        });

        return { linked: true, mode: "OUTREACH", outreachId: d.id };
      }

      await addDoc(collection(db, "unmatched_applications"), {
        applicationId,
        pacsName: pacsName || "",
        contactName: contactName || "",
        district: district || "",
        taluka: taluka || "",
        village: village || "",
        contactPhone: (contactPhone || "").trim(),
        contactEmail: (contactEmail || "").trim(),
        capacityMt: Number(capacityMt || 0) || null,
        source: "INVITE_CODE_NOT_FOUND",
        invitationCode: code,
        createdAt: now,
      });

      return { linked: false, mode: "UNMATCHED_INVITE_CODE_NOT_FOUND" };
    } catch (e) {
      console.error("linkApplicationToOutreachOrUnmatched error:", e);

      try {
        await addDoc(collection(db, "unmatched_applications"), {
          applicationId,
          pacsName: pacsName || "",
          contactName: contactName || "",
          district: district || "",
          taluka: taluka || "",
          village: village || "",
          contactPhone: (contactPhone || "").trim(),
          contactEmail: (contactEmail || "").trim(),
          capacityMt: Number(capacityMt || 0) || null,
          source: "LINKING_FAILED_FALLBACK",
          invitationCode: code,
          createdAt: now,
        });
      } catch (e2) {
        console.error("fallback unmatched save failed:", e2);
      }

      return { linked: false, mode: "LINKING_FAILED" };
    }
  }

  try {
    await addDoc(collection(db, "unmatched_applications"), {
      applicationId,
      pacsName: pacsName || "",
      contactName: contactName || "",
      district: district || "",
      taluka: taluka || "",
      village: village || "",
      contactPhone: (contactPhone || "").trim(),
      contactEmail: (contactEmail || "").trim(),
      capacityMt: Number(capacityMt || 0) || null,
      source: "DIRECT_SHINE_AGRI",
      createdAt: now,
    });
  } catch (e) {
    console.error("direct unmatched save failed:", e);
  }

  return { linked: false, mode: "DIRECT_SHINE_AGRI" };
}

/* ---------- Full Screen Modal ---------- */
function FullScreenModal({ open, title, onClose, children }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="absolute inset-0 p-3 md:p-6 flex">
        <div className="w-full max-w-6xl mx-auto rounded-3xl bg-white shadow-2xl border border-slate-200 overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
            <div className="text-sm md:text-base font-black text-slate-900">
              {title}
            </div>

            <button
              type="button"
              onClick={onClose}
              className="rounded-full bg-slate-900 text-white px-4 py-2 text-xs font-bold hover:bg-slate-800"
            >
              Close ✕
            </button>
          </div>

          <div className="flex-1 overflow-auto p-4 md:p-6 bg-gradient-to-br from-emerald-50 via-white to-teal-50">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================== MAIN COMPONENT ================== */

export default function GodownApplyForm() {
  const [showForm, setShowForm] = useState(false);

  const [pacsName, setPacsName] = useState("");
  const [pacsRegNo, setPacsRegNo] = useState("");
  const [pacsType, setPacsType] = useState("PACS");

  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [invitationCode, setInvitationCode] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  const [state, setState] = useState("Gujarat");
  const [district, setDistrict] = useState("");
  const [taluka, setTaluka] = useState("");
  const [village, setVillage] = useState("");

  const [capacityMt, setCapacityMt] = useState("1000");
  const [godownType, setGodownType] = useState("1000 MT Godown");

  const [plotLength, setPlotLength] = useState("");
  const [plotWidth, setPlotWidth] = useState("");
  const [landDetails, setLandDetails] = useState("");
  const [mapUrl, setMapUrl] = useState("");

  const [regFiles, setRegFiles] = useState([]);
  const [landFiles, setLandFiles] = useState([]);
  const [committeeFiles, setCommitteeFiles] = useState([]);
  const [auditFiles, setAuditFiles] = useState([]);
  const [otherFiles, setOtherFiles] = useState([]);

  const [reserveFund, setReserveFund] = useState("");
  const [buildingFund, setBuildingFund] = useState("");
  const [shareCapital, setShareCapital] = useState("");
  const [auditClass, setAuditClass] = useState("");

  const [profitRows, setProfitRows] = useState(() =>
    getLastCompletedFinancialYears(3).map((fy) => ({
      fy: fy.value,
      profit: "",
    }))
  );

  const [submitting, setSubmitting] = useState(false);

  const [checkId, setCheckId] = useState("");
  const [checkMobile, setCheckMobile] = useState("");
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);

  const [lastAppId, setLastAppId] = useState("");
  const [lastAppDate, setLastAppDate] = useState(null);

  const numericLength = Number(plotLength) || 0;
  const numericWidth = Number(plotWidth) || 0;
  const landArea = numericLength * numericWidth;

  const talukaOptions =
    TALUKAS_BY_DISTRICT[district] && TALUKAS_BY_DISTRICT[district].length
      ? TALUKAS_BY_DISTRICT[district]
      : [];

  const financialYearOptions = getLastCompletedFinancialYears(7);

  const updateProfitRow = (index, field, value) => {
    setProfitRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const hasDuplicateFy = (rows) => {
    const years = rows.map((r) => r.fy).filter(Boolean);
    return new Set(years).size !== years.length;
  };

  const totalProfit = profitRows.reduce(
    (sum, row) => sum + toAmount(row.profit),
    0
  );

  const averageProfit =
    profitRows.length > 0 ? Math.round(totalProfit / profitRows.length) : 0;

  const totalFunds =
    toAmount(reserveFund) + toAmount(buildingFund) + toAmount(shareCapital);

  const buildFinancialDetailsPayload = () => ({
    reserveFund: toAmount(reserveFund),
    buildingFund: toAmount(buildingFund),
    shareCapital: toAmount(shareCapital),
    auditClass: String(auditClass || "").trim(),

    profits: profitRows.map((row, index) => ({
      yearNo: index + 1,
      fy: row.fy,
      profit: toAmount(row.profit),
    })),

    totals: {
      totalProfit,
      averageProfit,
      totalFunds,
    },
  });

  const resetFinancialDetails = () => {
    setReserveFund("");
    setBuildingFund("");
    setShareCapital("");
    setAuditClass("");

    const defaults = getLastCompletedFinancialYears(3);

    setProfitRows(
      defaults.map((fy) => ({
        fy: fy.value,
        profit: "",
      }))
    );
  };

  const validate = () => {
    if (!pacsName || !pacsRegNo || !contactName || !contactPhone) {
      toast.error("Institution name, registration no. & contact details mandatory hain.");
      return false;
    }

    if (contactPhone.length !== 10) {
      toast.error("Mobile number 10 digit ka hona chahiye.");
      return false;
    }

    if (!district || !taluka || !village) {
      toast.error("District / Taluka / Village fill karo.");
      return false;
    }

    if (!capacityMt || isNaN(Number(capacityMt))) {
      toast.error("Capacity (MT) select karo.");
      return false;
    }

    if (hasDuplicateFy(profitRows)) {
      toast.error("Profit section me same financial year duplicate select mat karo.");
      return false;
    }

    if (profitRows.some((row) => !row.fy)) {
      toast.error("Last 3 years profit me financial year select karo.");
      return false;
    }

    if (profitRows.some((row) => String(row.profit).trim() === "")) {
      toast.error("Last 3 years profit amount fill karo. Loss ho to negative amount enter karo.");
      return false;
    }

    if (!regFiles.length) {
      toast.error("Registration Certificate upload required.");
      return false;
    }

    if (!landFiles.length) {
      toast.error("Land documents (7/12 / 8A / lease) upload required.");
      return false;
    }

    if (!committeeFiles.length) {
      toast.error("Committee/Board Resolution upload required.");
      return false;
    }

    if (!auditFiles.length) {
      toast.error("Last 3 years audit reports upload required.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setSubmitting(true);

      const normalizedDistrict = normalizeDistrictName(district);
      const normalizedTaluka = String(taluka || "").trim();
      const normalizedVillage = String(village || "").trim();

      const { appId, fy, seq } = await getNextGodownApplicationId();

      const uploadGroup = async (files, folder) => {
        const out = [];

        for (const file of files) {
          const path = `godown_applications/${appId.replace(/\//g, "_")}/${folder}/${Date.now()}_${file.name}`;
          const ref = storageRef(storage, path);

          await uploadBytes(ref, file);

          const url = await getDownloadURL(ref);

          out.push({
            name: file.name,
            url,
            uploadedAt: new Date().toISOString(),
          });
        }

        return out;
      };

      const registrationDocs = await uploadGroup(regFiles, "registration");
      const landDocs = await uploadGroup(landFiles, "land");
      const committeeDocs = await uploadGroup(committeeFiles, "committee");
      const auditDocs = await uploadGroup(auditFiles, "audit");
      const otherDocs = await uploadGroup(otherFiles, "other");

      const createdByUid = auth.currentUser?.uid || null;
      const now = serverTimestamp();
      const financialDetails = buildFinancialDetailsPayload();

      const applicationPayload = {
        applicationId: appId,
        applicationSeq: seq,
        financialYear: fy,

        source: "PACS",
        createdByUid,
        createdByRole: "PUBLIC",

        state,
        district: normalizedDistrict,
        taluka: normalizedTaluka,
        village: normalizedVillage,

        pacsName: pacsName.trim(),
        pacsRegistrationNo: pacsRegNo.trim(),
        pacsType,

        contactName: contactName.trim(),
        contactPhone: contactPhone.trim(),

        invitationCode: String(invitationCode || "").trim(),
        contactEmail: String(contactEmail || "").trim(),

        capacityMt: Number(capacityMt),
        godownType,

        plotLength: numericLength,
        plotWidth: numericWidth,
        landArea,
        landDetails: landDetails.trim(),
        googleMapUrl: mapUrl.trim(),

        financialDetails,

        documents: {
          registrationDocs,
          landDocs,
          committeeDocs,
          auditDocs,
          otherDocs,
        },

        status: "SUBMITTED",

        siteVisitStatus: "NEW",
        siteVisitAssignedTo: null,
        siteVisitPlanDate: null,
        siteVisitReportText: "",
        siteVisitDocs: [],

        pmcDecision: "PENDING",
        pmcDecisionRemark: "",
        pmcDecisionAt: null,

        workflowProjectId: null,
        workflowProgress: 0,
        workflowStage: null,
        workflowUpdatedAt: null,

        tenderStage: "NOT_STARTED",
        linkedTenderId: null,

        createdAt: now,
        updatedAt: now,
      };

      await addDoc(collection(db, "godown_projects"), applicationPayload);

      await linkApplicationToOutreachOrUnmatched({
        invitationCode,
        applicationId: appId,
        pacsName: pacsName.trim(),
        contactName: contactName.trim(),
        contactPhone: contactPhone.trim(),
        contactEmail: String(contactEmail || "").trim(),
        district: normalizedDistrict,
        taluka: normalizedTaluka,
        village: normalizedVillage,
        capacityMt: Number(capacityMt),
      });

      const createdAtDate = new Date();

      downloadApplicationPDF({
        applicationId: appId,
        financialYear: fy,
        pacsName,
        pacsRegistrationNo: pacsRegNo,
        pacsType,
        contactName,
        contactPhone,
        village: normalizedVillage,
        taluka: normalizedTaluka,
        district: normalizedDistrict,
        state,
        googleMapUrl: mapUrl,
        capacityMt,
        godownType,
        plotLength: numericLength,
        plotWidth: numericWidth,
        landArea,
        landDetails,
        financialDetails,
        createdAt: createdAtDate,
      });

      queuePmcNotificationOnNewApplication({
        applicationId: appId,
        pacsName: pacsName.trim(),
        pacsRegistrationNo: pacsRegNo.trim(),
        district: normalizedDistrict,
        taluka: normalizedTaluka,
        village: normalizedVillage,
        capacityMt: Number(capacityMt),
        contactName: contactName.trim(),
        contactPhone: contactPhone.trim(),
      });

      toast.success(`Application submitted successfully. Your Application ID is ${appId}.`);

      setLastAppId(appId);
      setLastAppDate(createdAtDate);

      setPacsName("");
      setPacsRegNo("");
      setPacsType("PACS");
      setContactName("");
      setContactPhone("");

      setInvitationCode("");
      setContactEmail("");

      setDistrict("");
      setTaluka("");
      setVillage("");
      setCapacityMt("1000");
      setGodownType("1000 MT Godown");
      setPlotLength("");
      setPlotWidth("");
      setLandDetails("");
      setMapUrl("");

      setRegFiles([]);
      setLandFiles([]);
      setCommitteeFiles([]);
      setAuditFiles([]);
      setOtherFiles([]);

      resetFinancialDetails();

      setShowForm(false);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Application submit failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusCheck = async (e) => {
    e.preventDefault();

    if (!checkId || !checkMobile) {
      toast.error("Application ID aur mobile dono fill karo.");
      return;
    }

    try {
      setChecking(true);

      const q = query(
        collection(db, "godown_projects"),
        where("applicationId", "==", checkId.trim()),
        where("contactPhone", "==", checkMobile.trim())
      );

      const snap = await getDocs(q);

      if (snap.empty) {
        setCheckResult(null);
        toast.error("Application not found. ID/Mobile check karo.");
        return;
      }

      setCheckResult(snap.docs[0].data());
    } catch (e) {
      console.error(e);
      toast.error("Status check failed.");
    } finally {
      setChecking(false);
    }
  };

  const renderStatusLine = (label, value) => (
    <div className="flex justify-between gap-3 text-[11px] md:text-[12px]">
      <span className="font-semibold text-slate-700">{label}</span>
      <span className="text-slate-700 text-right">{value || "—"}</span>
    </div>
  );

  const statusBadge = (result) => {
    if (!result) return null;

    const status = result.status || "SUBMITTED";

    let text = "Submitted";
    let color = "bg-amber-100 text-amber-800 border border-amber-200";

    if (status === "APPROVED") {
      text = "Approved";
      color = "bg-emerald-100 text-emerald-800 border border-emerald-200";
    } else if (status === "REJECTED") {
      text = "Rejected";
      color = "bg-red-100 text-red-800 border border-red-200";
    }

    return (
      <span className={cx("px-3 py-1 rounded-full text-[11px] font-semibold", color)}>
        Overall Status: {text}
      </span>
    );
  };

  return (
    <>
      <Toaster position="top-center" />

      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-100/60 pb-10 pt-8">
        <div className="max-w-5xl mx-auto px-3 md:px-4">
          <div className="rounded-3xl bg-white/95 border border-emerald-200 shadow-[0_25px_60px_rgba(16,185,129,0.18)] px-6 py-6 md:px-10 md:py-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700">
                  યોજના માહિતી (Scheme Details)
                </p>

                <h1 className="mt-2 text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight leading-tight">
                  “સહકારી ક્ષેત્રમાં વિશ્વની સૌથી મોટી અનાજ સંગ્રહ યોજના”
                </h1>

                <p className="mt-2 text-sm text-slate-700 leading-7">
                  આ પેજ પર માર્ગદર્શિકા મુજબ પાત્ર સંસ્થાઓ માટે અનાજ સંગ્રહ ગોડાઉન
                  પ્રોજેક્ટની ઓનલાઈન અરજી લેવામાં આવે છે. અંતિમ મંજૂરી{" "}
                  <b>DCDC</b>, બેંક appraisal તથા લાગુ સરકારી નિયમો મુજબ થશે.
                </p>

                <div className="mt-4 grid md:grid-cols-3 gap-3 text-[13px]">
                  <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                    <div className="text-[12px] font-extrabold text-emerald-900">
                      કોણ અરજી કરી શકે?
                    </div>

                    <ul className="mt-2 space-y-1 text-slate-700 leading-6">
                      <li>• PACS / સહકારી સંસ્થાઓ</li>
                      <li>• અન્ય પાત્ર સહકારી/સંસ્થાગત એકમ</li>
                    </ul>
                  </div>

                  <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                    <div className="text-[12px] font-extrabold text-emerald-900">
                      જમીન અંગે નિયમ
                    </div>

                    <div className="mt-2 text-slate-700 leading-6">
                      પોતાની જમીન અથવા <b>DCDC મંજૂરી બાદ</b> ન્યૂનતમ{" "}
                      <b>20 વર્ષ</b> કાયદેસર લીજ.
                    </div>
                  </div>

                  <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                    <div className="text-[12px] font-extrabold text-emerald-900">
                      પ્રક્રિયા (Steps)
                    </div>

                    <div className="mt-2 text-slate-700 leading-6">
                      અરજી → દસ્તાવેજ અપલોડ → Submit → PDF ડાઉનલોડ → Site visit /
                      DPR માટે સંપર્ક.
                    </div>
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="text-sm font-black text-slate-900">
                    જરૂરી દસ્તાવેજો (Required Documents)
                  </div>

                  <ul className="mt-2 text-sm text-slate-700 leading-7 list-disc pl-5">
                    <li>
                      <b>Registration Certificate</b> (latest)
                    </li>
                    <li>
                      <b>Land Documents</b> (7/12, 8A, sale/lease deed, mutation
                      entries, etc.)
                    </li>
                    <li>
                      <b>Board / Managing Committee Resolution</b>
                    </li>
                    <li>
                      <b>Last 3 years Audit Reports</b>
                    </li>
                    <li>
                      <b>Financial Details</b> - Reserve Fund, Building Fund,
                      Share Capital, Last 3 Years Profit
                    </li>
                    <li>Other supporting documents (optional)</li>
                  </ul>

                  <div className="mt-2 text-[12px] text-slate-600">
                    નોંધ: દસ્તાવેજો self-attested copies તરીકે અપલોડ કરવાના રહેશે.
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 leading-7">
                  <b>મહત્વપૂર્ણ સ્પષ્ટતા:</b>{" "}
                  આ પોર્ટલ પર અરજી કરવાથી તાત્કાલિક મંજૂરી મળતી નથી. અંતિમ મંજૂરી,
                  સબસિડી તથા બેંક ફાઇનાન્સિંગ{" "}
                  <b>DCDC / સક્ષમ અધિકારીઓ</b> દ્વારા લાગુ{" "}
                  <b>સરકારી માર્ગદર્શિકા (Margdarshika)</b> મુજબ જ આપવામાં આવશે.
                  પ્રોજેક્ટ માટેની અરજી પ્રક્રિયા, DPR, દસ્તાવેજીકરણ, ટેન્ડર સહાય
                  અને project monitoring{" "}
                  <b>Shine Agri Allied And Marketing Multistate Cooperative Society Ltd</b>{" "}
                  દ્વારા આપવામાં આવશે.
                </div>
              </div>

              <div className="w-full md:w-[340px]">
                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="text-[12px] font-extrabold text-slate-700 uppercase tracking-wider">
                    Apply Online
                  </div>

                  <div className="mt-2 text-sm font-black text-slate-900">
                    Ready to submit your application?
                  </div>

                  <div className="mt-2 text-sm text-slate-600 leading-6">
                    “Apply Now” ક્લિક કરીને ફોર્મ ખૂલે છે. Main page scroll કરવાની
                    જરૂર નહીં પડે.
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowForm(true)}
                    className="mt-4 w-full rounded-full bg-emerald-600 px-5 py-3 text-sm font-extrabold text-white shadow hover:bg-emerald-700"
                  >
                    Apply Now
                  </button>

                  <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-[11px] font-extrabold text-slate-700 uppercase tracking-wider">
                      PMC Helpdesk
                    </div>

                    <div className="mt-2 text-sm text-slate-700 leading-6">
                      જો કોઈ મદદ જોઈએ તો:
                      <br />
                      <b>Shine Agri Allied (PMC)</b>
                      <br />
                      📞 Mobile: 8490884020
                      <br />
                      ✉️ Email: tender@shineagri.org
                    </div>
                  </div>

                  {lastAppId && (
                    <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                      <div className="text-[11px] font-extrabold text-emerald-800 uppercase tracking-wider">
                        Last Submitted
                      </div>

                      <div className="mt-1 font-mono text-[12px] text-slate-900">
                        {lastAppId}
                      </div>

                      <div className="text-[12px] text-slate-700">
                        {lastAppDate
                          ? dayjs(lastAppDate).format("DD/MM/YYYY, hh:mm A")
                          : ""}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="text-sm font-black text-slate-900">
                    Application Status Check
                  </div>

                  <form onSubmit={handleStatusCheck} className="mt-3 grid gap-3">
                    <div>
                      <Label>Application ID</Label>
                      <Input
                        value={checkId}
                        onChange={setCheckId}
                        placeholder="SAAM/2526/Godown/0001"
                      />
                    </div>

                    <div>
                      <Label>Registered Mobile No.</Label>
                      <Input
                        value={checkMobile}
                        onChange={(v) =>
                          setCheckMobile(v.replace(/\D/g, "").slice(0, 10))
                        }
                        placeholder="10-digit mobile"
                      />
                    </div>

                    <button
                      disabled={checking}
                      className="w-full rounded-full bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
                    >
                      {checking ? "Checking…" : "Check Status"}
                    </button>
                  </form>

                  <div className="mt-3">{statusBadge(checkResult)}</div>

                  {checkResult && (
                    <div className="mt-3 grid gap-2">
                      {renderStatusLine("Institution", checkResult.pacsName)}
                      {renderStatusLine("District", checkResult.district)}
                      {renderStatusLine("Taluka", checkResult.taluka)}
                      {renderStatusLine("Village", checkResult.village)}
                      {renderStatusLine("Status", checkResult.status)}
                      {renderStatusLine("Site Visit", checkResult.siteVisitStatus)}
                      {renderStatusLine("PMC Decision", checkResult.pmcDecision)}
                      {renderStatusLine(
                        "Avg. Profit",
                        checkResult.financialDetails?.totals?.averageProfit
                          ? formatInr(checkResult.financialDetails.totals.averageProfit)
                          : "—"
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 text-center text-sm">
              <Link
                to="/"
                className="inline-flex items-center gap-1 text-emerald-700 underline-offset-4 hover:underline"
              >
                <span>← Back to Home</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <FullScreenModal
        open={showForm}
        onClose={() => setShowForm(false)}
        title="Godown Application Form (Online)"
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="rounded-3xl border border-emerald-200 bg-white/90 p-4 shadow-sm">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-emerald-700">
                  Apply in 5 minutes
                </div>

                <div className="mt-1 text-sm md:text-base font-black text-slate-900">
                  Keep society details, audit reports and last 3 years profit ready.
                </div>

                <div className="mt-1 text-[12px] text-slate-600 leading-6">
                  Submit ke baad PDF me formal request letter + profit average table
                  auto-generate hoga.
                </div>
              </div>

              <div className="mt-2 md:mt-0 rounded-2xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs">
                <div className="font-extrabold text-emerald-800">Support</div>
                <div className="text-slate-700">
                  Direct Shine Agri PMC Handholding
                </div>
              </div>
            </div>
          </div>

          <Card title="Institution Details">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Institution Name</Label>
                <Input
                  value={pacsName}
                  onChange={setPacsName}
                  placeholder="e.g. XYZ Seva Sahakari Mandali Ltd."
                />
              </div>

              <div>
                <Label>Registration No.</Label>
                <Input
                  value={pacsRegNo}
                  onChange={setPacsRegNo}
                  placeholder="e.g. REG/____/____"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Type of Institution</Label>
                <select
                  value={pacsType}
                  onChange={(e) => setPacsType(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white/70 px-4 py-2.5 text-sm shadow-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                >
                  <option value="PACS">PACS</option>
                  <option value="MSCS">Multi-State Cooperative</option>
                  <option value="FPC">Farmer Producer Company</option>
                  <option value="OTHER">Other Co-operative</option>
                </select>
              </div>
            </div>
          </Card>

          <Card title="Contact Person">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Contact Name</Label>
                <Input
                  value={contactName}
                  onChange={setContactName}
                  placeholder="Chairman / CEO / Secretary Name"
                />
              </div>

              <div>
                <Label>Mobile No.</Label>
                <Input
                  value={contactPhone}
                  onChange={(v) =>
                    setContactPhone(v.replace(/\D/g, "").slice(0, 10))
                  }
                  placeholder="10-digit mobile"
                />
                <div className="mt-1 text-[10px] text-slate-500">
                  Same number will be used for status tracking.
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Invitation Code (Optional)</Label>
                <Input
                  value={invitationCode}
                  onChange={(v) => setInvitationCode(v.trim())}
                  placeholder="e.g. SAAM-000123"
                />
                <div className="mt-1 text-[10px] text-slate-500">
                  Agar Shine Agri PMC se code mila hai to yaha dalein. Direct
                  applicants blank chhod sakte hain.
                </div>
              </div>

              <div>
                <Label>Email (Optional)</Label>
                <Input
                  value={contactEmail}
                  onChange={setContactEmail}
                  placeholder="example@email.com"
                />
              </div>
            </div>
          </Card>

          <Card title="Location Details">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>State</Label>
                <select
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white/70 px-4 py-2.5 text-sm shadow-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                >
                  <option value="Gujarat">Gujarat</option>
                </select>
              </div>

              <div>
                <Label>District</Label>
                <select
                  value={district}
                  onChange={(e) => {
                    const nextDistrict = normalizeDistrictName(e.target.value);
                    setDistrict(nextDistrict);
                    setTaluka("");
                  }}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white/70 px-4 py-2.5 text-sm shadow-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                >
                  <option value="">Select District</option>
                  {GUJ_DISTRICTS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Taluka</Label>
                <select
                  value={taluka}
                  onChange={(e) => setTaluka(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white/70 px-4 py-2.5 text-sm shadow-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                >
                  <option value="">Select Taluka</option>
                  {talukaOptions.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Village / Location</Label>
                <Input
                  value={village}
                  onChange={setVillage}
                  placeholder="e.g. Nilpur"
                />
              </div>
            </div>
          </Card>

          <Card title="Proposed Godown Details">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Capacity (Metric Tonnes)</Label>
                <select
                  value={capacityMt}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCapacityMt(v);
                    if (v) setGodownType(`${v} MT Godown`);
                  }}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white/70 px-4 py-2.5 text-sm shadow-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                >
                  <option value="">Select Capacity</option>
                  {CAPACITY_OPTIONS.map((mt) => (
                    <option key={mt} value={String(mt)}>
                      {mt} MT
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Godown Type</Label>
                <select
                  value={godownType}
                  onChange={(e) => setGodownType(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white/70 px-4 py-2.5 text-sm shadow-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                >
                  {CAPACITY_OPTIONS.map((mt) => (
                    <option key={mt} value={`${mt} MT Godown`}>
                      {mt} MT Godown
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-[12px] text-amber-900 leading-6">
              <b>Note:</b> Land details like survey number, 7/12, 8A and lease
              document may be uploaded in documents section below.
            </div>
          </Card>

          <Card title="Financial Strength Details">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-[12px] text-emerald-900 leading-6">
              <b>Note:</b> Last 3 years ke FY select karke profit enter karo. Loss
              ho to negative amount enter kar sakte ho. PDF me average profit table
              auto-generate hoga.
            </div>

            <div>
              <div className="text-sm font-black text-slate-900">
                Last 3 Years Profit
              </div>

              <div className="mt-3 grid gap-3">
                {profitRows.map((row, index) => (
                  <div
                    key={`profit-${index}`}
                    className="grid md:grid-cols-2 gap-3 rounded-2xl border border-slate-200 bg-white p-4"
                  >
                    <div>
                      <Label>Financial Year</Label>
                      <select
                        value={row.fy}
                        onChange={(e) =>
                          updateProfitRow(index, "fy", e.target.value)
                        }
                        className="mt-1 w-full rounded-xl border border-slate-200 bg-white/70 px-4 py-2.5 text-sm shadow-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                      >
                        <option value="">Select FY</option>
                        {financialYearOptions.map((fy) => (
                          <option key={fy.value} value={fy.value}>
                            {fy.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label>Net Profit Amount (₹)</Label>
                      <Input
                        type="number"
                        value={row.profit}
                        onChange={(v) => updateProfitRow(index, "profit", v)}
                        placeholder="e.g. 250000"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Reserve Fund (₹)</Label>
                <Input
                  type="number"
                  value={reserveFund}
                  onChange={setReserveFund}
                  placeholder="e.g. 1000000"
                />
              </div>

              <div>
                <Label>Building / Makan Fund (₹)</Label>
                <Input
                  type="number"
                  value={buildingFund}
                  onChange={setBuildingFund}
                  placeholder="e.g. 500000"
                />
              </div>

              <div>
                <Label>Share Capital (₹)</Label>
                <Input
                  type="number"
                  value={shareCapital}
                  onChange={setShareCapital}
                  placeholder="e.g. 1500000"
                />
              </div>

              <div>
                <Label>Audit Class</Label>
                <select
                  value={auditClass}
                  onChange={(e) => setAuditClass(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white/70 px-4 py-2.5 text-sm shadow-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                >
                  <option value="">Select Audit Class</option>
                  <option value="A">A Class</option>
                  <option value="B">B Class</option>
                  <option value="C">C Class</option>
                  <option value="D">D Class</option>
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                <div className="text-[11px] font-extrabold text-emerald-800 uppercase tracking-wider">
                  Total Profit
                </div>
                <div className="mt-1 text-lg font-black text-slate-900">
                  {formatInr(totalProfit)}
                </div>
              </div>

              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
                <div className="text-[11px] font-extrabold text-blue-800 uppercase tracking-wider">
                  Average Profit
                </div>
                <div className="mt-1 text-lg font-black text-slate-900">
                  {formatInr(averageProfit)}
                </div>
              </div>

              <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4">
                <div className="text-[11px] font-extrabold text-amber-800 uppercase tracking-wider">
                  Fund Strength
                </div>
                <div className="mt-1 text-lg font-black text-slate-900">
                  {formatInr(totalFunds)}
                </div>
              </div>
            </div>
          </Card>

          <Card title="Upload Supporting Documents (Required + Optional)">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-[12px] text-slate-700 leading-6">
              Required documents upload karna mandatory hai:
              <b> Registration</b>, <b> Land</b>, <b> Resolution</b>,{" "}
              <b>Audit</b>. Other supporting documents optional hain.
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <Label>Registration Certificate(s)</Label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setRegFiles(Array.from(e.target.files || []))}
                  className="mt-2 w-full text-xs file:mr-2 file:rounded-full file:border-0 file:bg-emerald-600 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white hover:file:bg-emerald-700"
                />
                {regFiles.length > 0 && (
                  <div className="mt-2 text-[11px] text-slate-600">
                    {regFiles.length} file(s) selected.
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <Label>Land Docs (7/12 / 8A / Lease)</Label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setLandFiles(Array.from(e.target.files || []))}
                  className="mt-2 w-full text-xs file:mr-2 file:rounded-full file:border-0 file:bg-emerald-600 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white hover:file:bg-emerald-700"
                />
                {landFiles.length > 0 && (
                  <div className="mt-2 text-[11px] text-slate-600">
                    {landFiles.length} file(s) selected.
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <Label>Committee / Board Resolution</Label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) =>
                    setCommitteeFiles(Array.from(e.target.files || []))
                  }
                  className="mt-2 w-full text-xs file:mr-2 file:rounded-full file:border-0 file:bg-emerald-600 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white hover:file:bg-emerald-700"
                />
                {committeeFiles.length > 0 && (
                  <div className="mt-2 text-[11px] text-slate-600">
                    {committeeFiles.length} file(s) selected.
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <Label>Last 3 Years Audit Reports</Label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setAuditFiles(Array.from(e.target.files || []))}
                  className="mt-2 w-full text-xs file:mr-2 file:rounded-full file:border-0 file:bg-emerald-600 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white hover:file:bg-emerald-700"
                />
                {auditFiles.length > 0 && (
                  <div className="mt-2 text-[11px] text-slate-600">
                    {auditFiles.length} file(s) selected.
                  </div>
                )}
              </div>

              <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-white p-4">
                <Label>Other Supporting Documents (Optional)</Label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setOtherFiles(Array.from(e.target.files || []))}
                  className="mt-2 w-full text-xs file:mr-2 file:rounded-full file:border-0 file:bg-emerald-600 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white hover:file:bg-emerald-700"
                />
                {otherFiles.length > 0 && (
                  <div className="mt-2 text-[11px] text-slate-600">
                    {otherFiles.length} file(s) selected.
                  </div>
                )}
              </div>
            </div>
          </Card>

          <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="text-[11px] md:text-[12px] text-slate-600">
              Submit ke baad PDF auto-download hoga. PDF me letter + profit average
              table included rahega.
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>

              <button
                disabled={submitting}
                className={cx(
                  "rounded-full px-6 py-2.5 text-sm font-semibold text-white shadow-lg transition-all duration-150",
                  submitting
                    ? "bg-emerald-400/80 cursor-not-allowed"
                    : "bg-emerald-600 hover:bg-emerald-700 hover:shadow-[0_18px_40px_rgba(16,185,129,0.35)]"
                )}
              >
                {submitting ? "Submitting…" : "Submit & Download PDF"}
              </button>
            </div>
          </div>
        </form>
      </FullScreenModal>
    </>
  );
}